import React, { useState, useCallback, useMemo } from 'react';
import { getEventManagers, createEventManager, updateEventManager, deleteEventManager, addEventToManager, recordScan } from '../services/api';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon,
  UserGroupIcon,
  QrCodeIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const Events = () => {
  const [selectedManager, setSelectedManager] = useState(null);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    status: 'active',
  });
  const [eventData, setEventData] = useState({
    name: '',
    date: '',
    location: '',
    description: '',
  });

  // ✅ React Query: Fetch event managers
  const {
    data: managers = [],
    isLoading,
    refetch: refetchManagers,
  } = useApiQuery(
    ['event-managers'],
    () => getEventManagers().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => console.error('Error fetching managers'),
    }
  );

  // ✅ React Query: Create manager mutation
  const createManagerMutation = useApiMutation(
    (data) => createEventManager(data).then(res => res.data),
    {
      onSuccess: () => {
        handleCloseManagerModal();
        refetchManagers();
      },
      onError: (error) => {
        console.error('Error saving manager:', error);
        alert(error.response?.data?.message || 'Error saving manager');
      },
    }
  );

  // ✅ React Query: Update manager mutation
  const updateManagerMutation = useApiMutation(
    ({ id, data }) => updateEventManager(id, data).then(res => res.data),
    {
      onSuccess: () => {
        handleCloseManagerModal();
        refetchManagers();
      },
      onError: (error) => {
        console.error('Error saving manager:', error);
        alert(error.response?.data?.message || 'Error saving manager');
      },
    }
  );

  // ✅ React Query: Delete manager mutation
  const deleteManagerMutation = useApiMutation(
    (id) => deleteEventManager(id).then(res => res.data),
    {
      onSuccess: () => {
        refetchManagers();
      },
      onError: (error) => {
        console.error('Error deleting manager:', error);
      },
    }
  );

  // ✅ React Query: Add event to manager mutation
  const addEventMutation = useApiMutation(
    ({ managerId, eventData }) => addEventToManager(managerId, eventData).then(res => res.data),
    {
      onSuccess: () => {
        handleCloseEventModal();
        refetchManagers();
      },
      onError: (error) => {
        console.error('Error adding event:', error);
      },
    }
  );

  // ✅ React Query: Record scan mutation
  const recordScanMutation = useApiMutation(
    ({ managerId, eventId }) => recordScan(managerId, eventId).then(res => res.data),
    {
      onSuccess: () => {
        refetchManagers();
        alert('Scan recorded successfully!');
      },
      onError: (error) => {
        console.error('Error recording scan:', error);
      },
    }
  );

  // ✅ Handlers
  const handleManagerSubmit = useCallback((e) => {
    e.preventDefault();
    if (editingManager) {
      updateManagerMutation.mutate({ id: editingManager._id, data: formData });
    } else {
      createManagerMutation.mutate(formData);
    }
  }, [editingManager, formData, createManagerMutation, updateManagerMutation]);

  const handleEventSubmit = useCallback((e) => {
    e.preventDefault();
    if (selectedManager) {
      addEventMutation.mutate({ 
        managerId: selectedManager._id, 
        eventData 
      });
    }
  }, [selectedManager, eventData, addEventMutation]);

  const handleDeleteManager = useCallback((id) => {
    if (window.confirm('Are you sure you want to delete this event manager?')) {
      deleteManagerMutation.mutate(id);
    }
  }, [deleteManagerMutation]);

  const handleEditManager = useCallback((manager) => {
    setEditingManager(manager);
    setFormData({
      name: manager.name,
      email: manager.email,
      password: '',
      phone: manager.phone || '',
      status: manager.status,
    });
    setShowManagerModal(true);
  }, []);

  const handleRecordScan = useCallback((managerId, eventId) => {
    recordScanMutation.mutate({ managerId, eventId });
  }, [recordScanMutation]);

  const handleCloseManagerModal = useCallback(() => {
    setShowManagerModal(false);
    setEditingManager(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      status: 'active',
    });
  }, []);

  const handleCloseEventModal = useCallback(() => {
    setShowEventModal(false);
    setSelectedManager(null);
    setEventData({
      name: '',
      date: '',
      location: '',
      description: '',
    });
  }, []);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading event managers...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createManagerMutation.isPending || 
                       updateManagerMutation.isPending || 
                       addEventMutation.isPending ||
                       recordScanMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Event Managers & Events</h1>
          <p className="text-sm text-gray-500 mt-1">Manage managers and their associated events</p>
        </div>
        <button
          onClick={() => setShowManagerModal(true)}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          Add Event Manager
        </button>
      </div>

      {managers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <UserGroupIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No event managers found</p>
          <button
            onClick={() => setShowManagerModal(true)}
            disabled={isSubmitting}
            className="mt-4 inline-flex items-center gap-2 text-[#1672b9] hover:underline disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
            Add your first manager
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {managers.map((manager) => (
            <div key={manager._id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-md">
              {/* Manager Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{manager.name}</h2>
                    <p className="text-sm text-gray-600">{manager.email}</p>
                    {manager.phone && <p className="text-sm text-gray-500 mt-0.5">📞 {manager.phone}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      manager.status === 'active'
                        ? 'bg-[#1672b9]/10 text-[#1672b9]'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {manager.status}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedManager(manager);
                        setShowEventModal(true);
                      }}
                      disabled={isSubmitting}
                      className="inline-flex items-center gap-1 bg-[#1672b9] text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Add Event
                    </button>
                    <button
                      onClick={() => handleEditManager(manager)}
                      disabled={isSubmitting}
                      className="p-1.5 text-gray-400 hover:text-[#1672b9] transition-colors disabled:opacity-50"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteManager(manager._id)}
                      disabled={deleteManagerMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Manager Statistics */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1672b9]/10 rounded-lg">
                    <CalendarIcon className="w-5 h-5 text-[#1672b9]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Events</p>
                    <p className="text-xl font-semibold text-gray-900">{manager.totalEventsManaged}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1672b9]/10 rounded-lg">
                    <UserGroupIcon className="w-5 h-5 text-[#1672b9]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Audience Recorded</p>
                    <p className="text-xl font-semibold text-gray-900">{manager.totalAudienceRecorded}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1672b9]/10 rounded-lg">
                    <QrCodeIcon className="w-5 h-5 text-[#1672b9]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Scans</p>
                    <p className="text-xl font-semibold text-gray-900">{manager.totalScans}</p>
                  </div>
                </div>
              </div>

              {/* Events List */}
              <div className="p-5">
                <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <ChartBarIcon className="w-5 h-5 text-gray-400" />
                  Events
                </h3>
                {!manager.events || manager.events.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                    <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No events added yet</p>
                    <button
                      onClick={() => {
                        setSelectedManager(manager);
                        setShowEventModal(true);
                      }}
                      disabled={isSubmitting}
                      className="mt-2 text-[#1672b9] text-sm hover:underline disabled:opacity-50"
                    >
                      + Add first event
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {manager.events.map((event, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 hover:shadow-sm transition-all">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 text-lg">{event.name}</h4>
                            <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {new Date(event.date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPinIcon className="w-3.5 h-3.5" />
                                {event.location}
                              </span>
                            </div>
                            {event.description && (
                              <p className="text-sm text-gray-500 mt-2 flex items-start gap-1">
                                <DocumentTextIcon className="w-3.5 h-3.5 mt-0.5" />
                                {event.description}
                              </p>
                            )}
                            <div className="flex gap-4 mt-2 text-xs">
                              <span className="bg-gray-50 px-2 py-1 rounded-md text-gray-600">
                                👥 Audience: {event.totalAudience}
                              </span>
                              <span className="bg-gray-50 px-2 py-1 rounded-md text-gray-600">
                                📱 Scans: {event.totalScans}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRecordScan(manager._id, event._id)}
                            disabled={recordScanMutation.isPending}
                            className="self-start inline-flex items-center gap-1 bg-[#1672b9] text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
                          >
                            <QrCodeIcon className="w-4 h-4" />
                            Record Scan
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Add/Edit Event Manager */}
      {showManagerModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                {editingManager ? 'Edit Event Manager' : 'Add Event Manager'}
              </h3>
              <button
                onClick={handleCloseManagerModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleManagerSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>
              {!editingManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseManagerModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editingManager ? 'Update Manager' : 'Create Manager')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add Event to Manager */}
      {showEventModal && selectedManager && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                Add Event for {selectedManager.name}
              </h3>
              <button
                onClick={handleCloseEventModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEventSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                <input
                  type="text"
                  value={eventData.name}
                  onChange={(e) => setEventData({ ...eventData, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={eventData.date}
                  onChange={(e) => setEventData({ ...eventData, date: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={eventData.location}
                  onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  rows="3"
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseEventModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? 'Adding...' : 'Add Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;